import React, { useState, useEffect, useRef } from "react";
import ReactTable from "react-table";
import "react-table/react-table.css";
import { withRouter } from "react-router";
import Api from "api/Api";
import SpringApi from "api/SpringApi";
import Loader from "components/common/Loader";
import Toaster from "components/common/Toaster";
import Button from "components/button/Button";

const API = "/api/v1/integrations";

const PROVIDERS = [
  { code: "eposnow", name: "Epos Now", icon: "fas fa-cash-register", color: "#3498db" },
  { code: "lightspeed", name: "Lightspeed", icon: "fas fa-bolt", color: "#e67e22" },
  { code: "clover", name: "Clover", icon: "fas fa-leaf", color: "#27ae60" }
];

const PosConnectionsTable = () => {
  const [connections, setConnections] = useState([]);
  const [stores, setStores] = useState([]);
  const [loader, setLoader] = useState(false);
  const [storesId, setStoresId] = useState("");
  const toaster = useRef();

  useEffect(() => {
    fetchConnections();
    fetchStores();
  }, []);

  const fetchStores = () => {
    Api.post("/admin/store/storeList", null)
      .then(res => {
        const list = res.data.data || [];
        setStores(list);
        // Auto-select if only one store
        if (list.length === 1) {
          setStoresId(String(list[0].storeId));
        }
      })
      .catch(err => {
        if (toaster.current) toaster.current.error("Failed to load stores");
      });
  };

  const fetchConnections = () => {
    setLoader(true);
    SpringApi.get(`${API}/connections`)
      .then(res => { setConnections(res.data.data || []); setLoader(false); })
      .catch(err => { if (toaster.current) toaster.current.error(err.message); setLoader(false); });
  };

  const handleConnect = (providerCode) => {
    if (!storesId) {
      if (toaster.current) toaster.current.error("Please select a store first");
      return;
    }
    const redirectUri = window.location.origin + "/pos-integrations";
    SpringApi.get(`${API}/${providerCode}/oauth/authorize`, {
      params: { storesId: parseInt(storesId, 10), redirectUri }
    })
      .then(res => {
        const authUrl = res.data.data && res.data.data.authUrl;
        if (authUrl) window.location.href = authUrl;
      })
      .catch(err => { if (toaster.current) toaster.current.error(err.message); });
  };

  const handleDisconnect = (id) => {
    if (!window.confirm("Disconnect this POS connection?")) return;
    SpringApi.delete(`${API}/connections/${id}`)
      .then(() => { if (toaster.current) toaster.current.success("Disconnected"); fetchConnections(); })
      .catch(err => { if (toaster.current) toaster.current.error(err.message); });
  };

  const columns = [
    { Header: "ID", accessor: "id", width: 60, className: "text-center", headerClassName: "react-table-header-class", filterable: false },
    { Header: "Provider", accessor: "providerDisplayName", className: "text-center", headerClassName: "react-table-header-class", filterable: false },
    {
      Header: "Store", accessor: "storesId", width: 160, className: "text-center", headerClassName: "react-table-header-class", filterable: false,
      Cell: row => {
        const store = stores.find(s => s.storeId === row.value);
        return store ? `${store.storeName} (${row.value})` : row.value;
      }
    },
    { Header: "External Store", accessor: "externalStoreId", className: "text-center", headerClassName: "react-table-header-class", filterable: false },
    {
      Header: "Status", accessor: "active", width: 90, className: "text-center", headerClassName: "react-table-header-class", filterable: false,
      Cell: row => (
        <span style={{ padding: "4px 10px", borderRadius: 12, fontSize: 12, fontWeight: 600,
          backgroundColor: row.value ? "#d4edda" : "#f8d7da", color: row.value ? "#155724" : "#721c24" }}>
          {row.value ? "Active" : "Inactive"}
        </span>
      )
    },
    {
      Header: "Tokens", accessor: "hasTokens", width: 80, className: "text-center", headerClassName: "react-table-header-class", filterable: false,
      Cell: row => <i className={`fas fa-${row.value ? "check-circle" : "times-circle"}`} style={{ color: row.value ? "#27ae60" : "#c0392b", fontSize: 16 }} />
    },
    {
      Header: "Action", accessor: "id", id: "action", width: 120, className: "text-center", headerClassName: "react-table-header-class", filterable: false,
      Cell: row => (
        <Button className="c-btn c-danger" onClick={() => handleDisconnect(row.value)}>
          <div className="fs-14 medium-text"><i className="fas fa-unlink mr-6" /> Disconnect</div>
        </Button>
      )
    }
  ];

  return (
    <div className="plr-15">
      {/* Connect Buttons */}
      <div className="roe-card-style mtb-15">
        <div className="roe-card-header module-header">
          <div className="flex-1 fs-16 demi-bold-text"><span className="hash"># </span> Connect a POS System</div>
        </div>
        <div className="roe-card-body" style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
          <div>
            <label className="fs-14 medium-text">Select Store</label>
            <select
              value={storesId}
              onChange={e => setStoresId(e.target.value)}
              className="form-control react-form-input"
              style={{ width: 220 }}
            >
              <option value="">-- Select a Store --</option>
              {stores.map(s => (
                <option key={s.storeId} value={s.storeId}>
                  {s.storeName} (ID: {s.storeId})
                </option>
              ))}
            </select>
          </div>
          {PROVIDERS.map(p => (
            <Button key={p.code} className="c-btn" onClick={() => handleConnect(p.code)}
              style={{ backgroundColor: p.color, color: "#fff", marginTop: 18 }}>
              <i className={`${p.icon} mr-6`} /> Connect {p.name}
            </Button>
          ))}
        </div>
      </div>

      {/* Connections Table */}
      <div className="roe-card-style mtb-15">
        <div className="roe-card-header module-header">
          <div className="flex-1 fs-16 demi-bold-text"><span className="hash"># </span> Active Connections</div>
          <Button className="c-btn ma-5" onClick={fetchConnections} style={{ backgroundColor: "#0f172a", color: "#fff" }}>
            <i className="fas fa-sync mr-6" /> Refresh
          </Button>
        </div>
        <div className="roe-card-body">
          <ReactTable style={{ border: "none", boxShadow: "none" }} data={connections} columns={columns}
            defaultPageSize={10} minRows={2} className="-striped -highlight custom-react-table-theme-class"
            loading={loader} LoadingComponent={Loader} />
        </div>
        <Toaster ref={toaster} />
      </div>
    </div>
  );
};

export default withRouter(PosConnectionsTable);

